import './styles.scss';

import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Discount } from '@global/types/types';
import { ToggleButton } from './components/Toggle';
import { FreeTrialTerms } from '../FreeTrialTerms';
import { InfoSection } from '../InfoSection';
import { OrderSummaryData } from './types';

interface ItemProps {
  value?: string;
  title?: string;
  customCls?: string;
}

const Row: FC<ItemProps> = ({ value, title, customCls = '' }) => {
  return value ? (
    <div className={`order-summary__item ${customCls}`}>
      <span>{title}</span>
      <strong>{value}</strong>
    </div>
  ) : null;
};

interface OrderSummaryProps {
  data: OrderSummaryData;
  loading: boolean;
  isFreeTrial: boolean;
  isMobile?: boolean;
  isShippingVisible?: boolean;
  discounts: Discount[];
}

export const OrderSummary = ({
  data,
  loading,
  isMobile,
  discounts,
  isFreeTrial,
  isShippingVisible = false,
}: OrderSummaryProps) => {
  const { t } = useTranslation('', { keyPrefix: 'checkout.summary' });
  const [isShow, setIsShow] = useState(false);

  const { productTotalPrice, shipping, vat, discount, tax, orderTotal } = data;

  const showToggle = useCallback(() => {
    setIsShow((prev) => !prev);
  }, []);

  if (loading) {
    return (
      <div className="order-summary-wrapper">
        <div className="order-summary">
          <div className="spinner" id="spinner" data-testid="spinner"></div>
        </div>
      </div>
    );
  }

  const hideCls = isShow
    ? 'order-summary__content-show'
    : 'order-summary__content-hide';

  return (
    <div className="order-summary-wrapper">
      <div className="order-summary">
        <div className="order-summary__header">
          <h2 className="order-summary__title">{t('title')}</h2>
          <ToggleButton isShow={isShow} toggle={showToggle} />
        </div>

        <div className="order-summary__content">
          <div className={hideCls}>
            <Row value={productTotalPrice} title={t('productTotal')} />
            {isShippingVisible && (
              <Row value={shipping} title={t('shipping')} />
            )}
            <Row value={vat} title={t('vat')} />
            <Row value={discount} title={t('discount')} />

            {isFreeTrial && (
              <div className="order-summary__item">{t('freeTrial')}</div>
            )}

            <div className="order-summary__item">
              {tax ? (
                <>
                  <span>{t('tax')}</span>
                  <strong>{tax}</strong>
                </>
              ) : (
                <i>
                  {shipping
                    ? t('tax_placeholder')
                    : t('shipping_tax_placeholder')}
                </i>
              )}
            </div>
            {isFreeTrial && isMobile && (
              <FreeTrialTerms discount={discounts[0]} />
            )}
          </div>

          <Row
            value={orderTotal}
            title={t('orderTotal')}
            customCls={'order-summary__total-price'}
          />
        </div>
      </div>
      {isFreeTrial && !isMobile && <FreeTrialTerms discount={discounts[0]} />}
      {!isMobile && <InfoSection />}
    </div>
  );
};
